import { CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  brand?: string
  className?: string
}

/**
 * Logo officiel du brand carte (Visa, Mastercard, Amex, Discover, JCB,
 * UnionPay, Diners). Fallback sur l'icône générique CreditCard pour les
 * brands inconnus. Les SVGs sont des reprises stylisées des logos
 * officiels, pas de dépendance externe.
 *
 * Brand exact attendu : valeurs Stripe (`visa`, `mastercard`, `amex`,
 * `discover`, `diners`, `jcb`, `unionpay`, `unknown`).
 */
export function CardBrandIcon({ brand, className }: Props) {
  const normalized = (brand || '').toLowerCase().trim()
  const cls = cn('h-6 w-9', className)

  switch (normalized) {
    case 'visa':
      return <VisaLogo className={cls} />
    case 'mastercard':
      return <MastercardLogo className={cls} />
    case 'amex':
    case 'american express':
    case 'american_express':
      return <AmexLogo className={cls} />
    case 'discover':
      return <DiscoverLogo className={cls} />
    case 'diners':
    case 'diners club':
    case 'diners_club':
      return <DinersLogo className={cls} />
    case 'jcb':
      return <JcbLogo className={cls} />
    case 'unionpay':
    case 'union pay':
      return <UnionPayLogo className={cls} />
    case 'link':
      return <LinkLogo className={cls} />
    default:
      return (
        <div
          className={cn(
            'flex items-center justify-center rounded bg-slate-200 dark:bg-slate-700',
            cls,
          )}
        >
          <CreditCard className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
        </div>
      )
  }
}

function VisaLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="Visa">
      <rect width="36" height="24" rx="3" fill="#1A1F71" />
      <path
        d="M14.5 8.8h-2.7l-1.7 6.4h2.7l1.7-6.4zM21.3 8.95c-.55-.21-1.4-.45-2.45-.45-2.7 0-4.6 1.35-4.6 3.28 0 1.42 1.36 2.21 2.4 2.69 1.06.48 1.42.79 1.42 1.22 0 .66-.84.96-1.6.96-1.07 0-1.65-.15-2.55-.52l-.36-.16-.39 2.16c.64.27 1.83.5 3.07.51 2.87 0 4.74-1.34 4.76-3.4.01-1.13-.71-1.99-2.26-2.7-.94-.46-1.5-.77-1.5-1.23 0-.41.5-.85 1.58-.85.9-.02 1.55.18 2.06.39l.25.11.42-2.01zM27.32 8.8h-2.08c-.65 0-1.13.18-1.41.83l-4 9.57h2.83l.57-1.51h3.45l.32 1.51h2.5l-2.18-10.4zM23.69 13.6c.22-.55 1.07-2.69 1.07-2.69-.02.03.22-.56.35-.92l.18.84.62 2.77h-2.22zM9.4 8.8L6.74 13.16 6.46 11.84c-.5-1.53-2.02-3.19-3.73-4.01l2.42 7.39h2.87l4.26-6.42H9.4z"
        fill="#fff"
      />
    </svg>
  )
}

function MastercardLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="Mastercard">
      <rect width="36" height="24" rx="3" fill="#1A1F36" />
      <circle cx="14.5" cy="12" r="5.5" fill="#EB001B" />
      <circle cx="21.5" cy="12" r="5.5" fill="#F79E1B" />
      <path
        d="M18 7.5a5.49 5.49 0 0 1 0 9 5.49 5.49 0 0 1 0-9z"
        fill="#FF5F00"
      />
    </svg>
  )
}

function AmexLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="American Express">
      <rect width="36" height="24" rx="3" fill="#006FCF" />
      <text
        x="18"
        y="15"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="6"
        fill="#fff"
        letterSpacing="0.5"
      >
        AMEX
      </text>
    </svg>
  )
}

function DiscoverLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="Discover">
      <rect width="36" height="24" rx="3" fill="#fff" stroke="#E5E7EB" />
      <path d="M0 13h36v6a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-6z" fill="#FF6000" />
      <text
        x="18"
        y="11"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="5"
        fill="#1A1F36"
      >
        DISCOVER
      </text>
    </svg>
  )
}

function DinersLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="Diners Club">
      <rect width="36" height="24" rx="3" fill="#0079BE" />
      <circle cx="18" cy="12" r="6" fill="#fff" />
      <path d="M15 7.5a5 5 0 0 0 0 9V7.5zm6 0a5 5 0 0 1 0 9v-9z" fill="#0079BE" />
    </svg>
  )
}

function JcbLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="JCB">
      <rect width="36" height="24" rx="3" fill="#fff" stroke="#E5E7EB" />
      <rect x="6" y="6" width="7" height="12" rx="1" fill="#0E4C96" />
      <rect x="14.5" y="6" width="7" height="12" rx="1" fill="#BE0029" />
      <rect x="23" y="6" width="7" height="12" rx="1" fill="#108E20" />
      <text
        x="18"
        y="14.5"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="5"
        fill="#fff"
      >
        JCB
      </text>
    </svg>
  )
}

function UnionPayLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="UnionPay">
      <rect width="36" height="24" rx="3" fill="#fff" stroke="#E5E7EB" />
      <text
        x="18"
        y="14"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="5.5"
        fill="#003473"
        letterSpacing="-0.2"
      >
        UnionPay
      </text>
    </svg>
  )
}

/** Link (Stripe) : carré vert, flèche noire vers la droite. */
function LinkLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 36 24" className={className} role="img" aria-label="Link">
      <rect width="36" height="24" rx="3" fill="#00D66F" />
      <path d="M13 6.5 23 12l-10 5.5v-3.6L17.6 12 13 10.1z" fill="#011E0F" />
    </svg>
  )
}
